import { useEffect, useState } from "react";
import Lenis from "lenis";
import "@/App.css";
import { Hero } from "@/components/Hero";
import { FaqBento } from "@/components/FaqBento";
import { FaqModal } from "@/components/FaqModal";
import { Marquee } from "@/components/Marquee";
import { HeaderMark } from "@/components/NineDotLogo";
import { GeoLocation } from "@/components/GeoLocation";
import { Instagram, Facebook, Youtube, Linkedin, Play, Apple, Mail } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";

const STORES = [
    { id: "android", href: "https://play.google.com/store/apps/details?id=com.cleard.app", Icon: Play, label: "Google Play" },
    { id: "ios", href: "https://apps.apple.com/app/clear-d", Icon: Apple, label: "App Store" },
];

const SOCIALS = [
    { id: "whatsapp", href: "https://wa.me/919666642654", Icon: FaWhatsapp },
    { id: "email", href: "mailto:contact@clear-d.com", Icon: Mail },
    { id: "instagram", href: "https://www.instagram.com/clear-d", Icon: Instagram },
    { id: "facebook", href: "https://www.facebook.com/clear_d", Icon: Facebook },
    { id: "youtube", href: "https://www.youtube.com/@clear-d", Icon: Youtube },
    { id: "linkedin", href: "https://www.linkedin.com/company/clear_d", Icon: Linkedin },
];

function App() {
    const [activeFaq, setActiveFaq] = useState(null);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
        let frame;
        const raf = (time) => {
            lenis.raf(time);
            frame = requestAnimationFrame(raf);
        };
        frame = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(frame);
            lenis.destroy();
        };
    }, []);

    return (
        <div className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-[#FAFBFD] font-body text-slate-900 select-none">
            <header className="z-30 flex h-14 shrink-0 items-center justify-between border-b border-slate-200/80 bg-white/70 px-4 backdrop-blur-md sm:h-16 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center gap-3"
                    data-testid="header-clear-d-logo"
                >
                    <HeaderMark size={1.2} />
                    <GeoLocation />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    className="flex items-center gap-4"
                >
                    <div className="flex items-center gap-1 sm:gap-2">
                        {SOCIALS.map(({ id, href, Icon }) => (
                            <a
                                key={id}
                                data-testid={`social-link-${id}`}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Clear D on ${id}`}
                                className="rounded-full p-1.5 text-slate-400 transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-50 hover:text-teal-600 sm:p-2"
                            >
                                <Icon size={16} strokeWidth={1.8} />
                            </a>
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        {STORES.map(({ id, href, Icon, label }) => (
                            <a
                                key={id}
                                data-testid={`store-button-${id}`}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Download Clear D on ${label}`}
                                className="group flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-500 hover:shadow-[0_4px_14px_-4px_rgba(13,148,136,0.35)] sm:gap-2 sm:px-3"
                            >
                                <Icon size={14} className="text-slate-700 transition-colors group-hover:text-teal-600" fill="currentColor" strokeWidth={0} />
                                <span className="hidden font-heading text-[11px] font-bold tracking-tight text-slate-800 sm:inline">
                                    {label}
                                </span>
                            </a>
                        ))}
                    </div>
                </motion.div>
            </header>

            <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 sm:p-6 lg:gap-6 lg:p-8">
                <Hero />
                <FaqBento onOpen={setActiveFaq} />
            </main>

            <Marquee />

            <FaqModal
                activeIndex={activeFaq}
                onClose={() => setActiveFaq(null)}
                onNavigate={setActiveFaq}
            />
        </div>
    );
}

export default App;
